import { haversineMeters } from './distance-meters';
import type {
  ConvoyCluster,
  ConvoyMemberInput,
  ConvoyProximityOptions,
} from './types';

/**
 * Proximity clustering for one shipment's devices (single-linkage / connected
 * components). Does not choose a pin pose — pass only members of one convoy.
 *
 * Clusters sorted by id (stable).
 */
export function clusterConvoyByProximity(
  members: readonly ConvoyMemberInput[],
  options: ConvoyProximityOptions,
): ConvoyCluster[] {
  const valid = members.filter(
    (m) =>
      Number.isFinite(m.lat) &&
      Number.isFinite(m.lng) &&
      Number.isFinite(m.deviceId) &&
      m.deviceId > 0,
  );
  const n = valid.length;
  if (n === 0) return [];

  const parent = Array.from({ length: n }, (_, i) => i);

  function find(i: number): number {
    let root = i;
    while (parent[root] !== root) root = parent[root]!;
    let cur = i;
    while (parent[cur] !== root) {
      const next = parent[cur]!;
      parent[cur] = root;
      cur = next;
    }
    return root;
  }

  function union(i: number, j: number): void {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[rj] = ri;
  }

  const radius = options.radiusMeters;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const a = valid[i]!;
      const b = valid[j]!;
      if (
        haversineMeters(
          { lat: a.lat, lng: a.lng },
          { lat: b.lat, lng: b.lng },
        ) <= radius
      ) {
        union(i, j);
      }
    }
  }

  const groups = new Map<number, ConvoyMemberInput[]>();
  for (let i = 0; i < n; i++) {
    const root = find(i);
    const list = groups.get(root);
    if (list) list.push(valid[i]!);
    else groups.set(root, [valid[i]!]);
  }

  const clusters: ConvoyCluster[] = [];
  for (const group of groups.values()) {
    const memberDeviceIds = group
      .map((m) => m.deviceId)
      .sort((a, b) => a - b);
    clusters.push({
      id: memberDeviceIds.join('-'),
      memberDeviceIds,
      members: group,
    });
  }

  clusters.sort((a, b) => a.id.localeCompare(b.id));
  return clusters;
}
